import { callGetApi, callPostApi } from "@/utils/apiWrapper";
import api from "@/utils/api";
import InsuranceAcConstant from "@/mixins/insurance/const/insuranceAcConstant";

// 자격증명은 persist되는 Vuex state에 넣지 않는다. 캐시 소유자 비교용 메모리만 사용한다.
let cacheSession = null;
let cacheEpoch = 0;
const currentSession = () => localStorage.getItem("naezip_to_ac_se");

// 퍼스트아메리칸(FA) 권원보험 — 회원 본인 청약 조회(NZSC-3189 알리미 섹션·가입정보).
// GET /api/v1/firstam/applications (AuthGuard) — NONE 제외, 최신 먼저.

export const state = {
  _firstamApps: [],
  _firstamLoaded: false,
};

export const mutations = {
  resetFirstamApps(state) {
    state._firstamApps = [];
    state._firstamLoaded = false;
  },
  setFirstamApps(state, apps) {
    state._firstamApps = Array.isArray(apps) ? apps : [];
    state._firstamLoaded = true;
  },
};

export const actions = {
  async acFirstamMyApplications({ commit, state }, { force = false } = {}) {
    const session = currentSession();
    if (session && cacheSession === session && state._firstamLoaded && !force)
      return state._firstamApps;
    if (cacheSession !== session) {
      cacheSession = session;
      cacheEpoch += 1;
      commit("resetFirstamApps");
    }
    if (!session) return [];
    const epoch = cacheEpoch;
    try {
      // callGetApi 는 응답 봉투를 깐 data(배열)를 반환(실패 시 null)
      const apps = await callGetApi({ url: "/api/v1/firstam/applications" });
      if (epoch !== cacheEpoch || session !== currentSession()) return [];
      const list = Array.isArray(apps) ? apps : [];
      commit("setFirstamApps", list);
      return list;
    } catch (e) {
      // 조회 실패는 알리미 화면을 막지 않음(FA 섹션만 미노출)
      if (epoch === cacheEpoch && session === currentSession()) commit("setFirstamApps", []);
      return [];
    }
  },

  // 사고 접수 요청 — POST /api/v1/firstam/claim (AuthGuard 가 user_no 주입).
  // reportSeq 전달 시 해당 청약, 없으면 서버가 증권발급 건/최신 선택.
  // 성공 시 { ok, applicationSeq } 반환, 실패 시 null.
  async acFirstamSubmitClaim(_ctx, { reportSeq = null } = {}) {
    const data = {};
    if (reportSeq != null) data.report_seq = reportSeq;
    return await callPostApi({ url: "/api/v1/firstam/claim", data });
  },

  // 랜딩 상담 신청(NZSC-3424) — POST /api/v1/firstam/consult-lead.
  // 랜딩은 라우트 가드가 없어 비로그인도 들어오므로 익명 허용 엔드포인트다(토큰 있으면 서버가 user_no 를 붙임).
  // phone 은 화면에선 하이픈으로 보여주되 전송은 숫자만 — 왓슨 dedup 이 phone UNIQUE 라 표기 흔들리면 중복 행이 생긴다.
  // 성공 판정은 apiWrapper 규약(code===0 → data, 실패 → null)에 맡긴다.
  async acFirstamSubmitConsultLead(_ctx, { name, phone } = {}) {
    const res = await callPostApi({
      url: InsuranceAcConstant.POST_FIRSTAM_CONSULT_LEAD,
      data: {
        name: (name || "").trim(),
        phone: (phone || "").replace(/\D/g, ""),
        agreed: true,
      },
    });
    return res !== null;
  },

  // 권원보험 가입 대기 신청(NZSC-4248) — 가장 최근 신청 1건. 계약상태 "둘러보는 중" → 재신청 모달 판단에만 쓴다
  // (신청 화면·저장은 web-core). 랜딩 상담 문의·채팅 문의 이력은 기준이 아니다.
  // GET /api/v1/firstam/waitlist/latest → { hasApplied, latest: null | { applyDate, movePeriod, phone } }
  // 실패 시 null(apiWrapper 규약) — 호출부가 판단한다.
  async acFirstamWaitlistLatest() {
    return await callGetApi({ url: InsuranceAcConstant.GET_FIRSTAM_WAITLIST_LATEST });
  },

  // 잔금일 미정 가입 경로(NZSC-3816) — 계약 정보 입력. POST /api/v1/firstam/contract-info.
  // 계약체결일·잔금일·정확한 거래 금액 세 값을 한 번에 저장(부분 입력은 BE 400).
  // BE 검증 메시지(잔금일 범위 등) 노출을 위해 api 직접 호출(acFirstamSubmitChange 와 동일 이유).
  // payload: { cont_dt:'YYYYMMDD', balance_dt:'YYYYMMDD', insu_amt:number,
  //            application_seq?(대상 청약 지목 1순위), report_seq?(승인 후에만 존재) }
  // 반환: { ok, applicationSeq, bandMismatch } | { ok:false, message }
  async acFirstamSubmitContractInfo(_ctx, payload = {}) {
    try {
      const res = await api.post("/api/v1/firstam/contract-info", payload);
      if (res?.data?.code === 0) return { ok: true, ...(res.data.data || {}) };
      return { ok: false, message: res?.data?.message || null };
    } catch (e) {
      if (e?.response?.status === 401) localStorage.removeItem("naezip_to_ac_se");
      const m = e?.response?.data?.message;
      return { ok: false, message: typeof m === "string" ? m : m?.message || null };
    }
  },

  // 부동산 정보 변경 — POST /api/v1/firstam/change (AuthGuard 가 user_no 주입).
  // 미청약=내부 자체변경 / 청약완료=FA 재청약. 변경 필드(보낸 것만) + 사유.
  // payload: { applicationSeq, reportSeq, reg_addr, property_no, est_typ, cont_dt, balance_dt,
  //            insu_amt, reason }
  // BE 검증 메시지(주소 변경 불가·잔금일 과거 등)를 노출해야 해서 api 직접 호출(callPostApi 는 실패 시 null·메시지 소실).
  // 반환: { ok, applicationSeq, mode } | { ok:false, message }
  //
  // ⚠️ application_seq = 대상 청약 지목(서버 1순위 선택자). 구간(잔금일 미정) 결제 건은 승인 전까지
  //    report_seq 가 null 이라 report_seq 만으로는 특정이 안 된다 — 구간 결제 2건 보유 유저가
  //    집 A 의 금액을 고치면 집 B 에 저장된다(이 PR 의 '보험가입금액 수정' 버튼이 그 진입점).
  //    contract-info API 와 같은 파라미터명(application_seq)·같은 우선순위 규칙을 쓴다.
  //    값이 없으면 기존처럼 미전송(서버가 report_seq → 자동 선택으로 폴백).
  async acFirstamSubmitChange(_ctx, payload = {}) {
    const data = {};
    if (payload.applicationSeq != null) data.application_seq = payload.applicationSeq;
    if (payload.reportSeq != null) data.report_seq = payload.reportSeq;
    ["reg_addr", "property_no", "est_typ", "cont_dt", "balance_dt", "insu_amt", "reason"].forEach(
      k => {
        if (payload[k] != null && payload[k] !== "") data[k] = payload[k];
      },
    );
    try {
      const res = await api.post("/api/v1/firstam/change", data);
      if (res?.data?.code === 0) return { ok: true, ...(res.data.data || {}) };
      return { ok: false, message: res?.data?.message || null };
    } catch (e) {
      if (e?.response?.status === 401) localStorage.removeItem("naezip_to_ac_se");
      const m = e?.response?.data?.message;
      return { ok: false, message: typeof m === "string" ? m : m?.message || null };
    }
  },
};

export const getters = {
  // 알리미 FA 섹션 대상 = 거절/취소/철회 제외 전체(다건 — 청약별 카드 스택)
  activeFirstamApps(state) {
    const HIDDEN = ["REJECTED", "CANCELLED", "WITHDRAWN"];
    return state._firstamApps.filter(a => !HIDDEN.includes(a.current_stat));
  },
  // 최신 1건(단건 폴백용 — 가입정보 self-load 등)
  activeFirstamApp(state, getters) {
    return getters.activeFirstamApps[0] || null;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
